'use client';

/**
 * ItemCard -- reusable item row card.
 *
 * Layout:
 *   [color strip] [thumbnail] [title + subtitle + annotation] [status pills]
 *
 * The left color strip is driven by the item's category slug/name via
 * getCategoryColor(). Pass `category` whenever it's available; omitting it
 * renders a neutral strip.
 *
 * All interactive elements (status pills) call stopPropagation so they don't
 * trigger the wrapping Link navigation.
 *
 * Selection mode (bulk actions):
 *   Pass isSelectionMode=true to enable. In selection mode:
 *     - The card is rendered as a div (not a Link).
 *     - A checkbox overlay appears in the top-left.
 *     - Tapping the card toggles its selected state via onToggleSelect.
 *   Long-pressing in normal mode triggers onLongPress, which the parent uses
 *   to enter selection mode with this item pre-selected.
 */

import { useRef, type ReactNode } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Check } from 'lucide-react';
import { cn, toHttps } from '@/lib/utils';
import { getCategoryColor } from '@/components/favely/category-emoji';
import { InlineStatusPills } from './status-pills';
import { SocialProofBadge } from './social-proof-badge';
import { ListPills } from './list-pills';
import { Skeleton } from '@/components/ui/skeleton';

const LONG_PRESS_MS = 400;

// Tiny 1×1 transparent PNG — used as blur placeholder for remote images.
const BLUR_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

interface ItemCardProps {
  itemId: string;
  title: string;
  /** Secondary line: e.g. author, year, cuisine type. */
  subtitle?: string;
  /** Personal annotation the viewer added when saving this item. */
  annotation?: string;
  thumbnail?: string | null;
  /** Category slug or name — drives the left color strip. */
  category?: string | null;
  /** Number of friends/network members who also saved this item. */
  networkCount?: number;
  /** Show Done/Liked status pills. Set false in non-auth contexts. */
  showStatus?: boolean;
  /** Override the default /items/[itemId] link. */
  href?: string;
  /** Lists this item belongs to — renders clickable pills inside the card. */
  lists?: { collectionId: string; collectionTitle: string }[];
  /** Optional content rendered below title/subtitle/annotation, always visible. */
  footer?: ReactNode;
  className?: string;
  // ── Selection mode props ────────────────────────────────────────────────
  /** When true the card renders in bulk-select mode (checkbox, no navigation). */
  isSelectionMode?: boolean;
  /** Whether this card is currently selected. */
  isSelected?: boolean;
  /** Called when the user taps the card in selection mode. */
  onToggleSelect?: (itemId: string) => void;
  /** Called after a long-press in normal mode (parent enters selection mode). */
  onLongPress?: (itemId: string) => void;
}

export function ItemCard({
  itemId,
  title,
  subtitle,
  annotation,
  thumbnail,
  category,
  networkCount = 0,
  showStatus = true,
  href,
  lists,
  footer,
  className,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
  onLongPress,
}: ItemCardProps) {
  const link = href ?? `/items/${itemId}`;
  const categoryColor = getCategoryColor(category);

  const stripColor = categoryColor;

  // ── Long-press detection (pointer events, works on touch + mouse) ────────
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePointerDown = () => {
    if (!onLongPress || isSelectionMode) return;
    longPressTimer.current = setTimeout(() => {
      onLongPress(itemId);
    }, LONG_PRESS_MS);
  };

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // ── Shared card interior ────────────────────────────────────────────────
  const cardInner = (
    <>
      {/* Category color strip — always visible */}
      <div
        className="w-1 shrink-0 transition-colors"
        style={{ backgroundColor: isSelected ? 'var(--primary)' : stripColor }}
        aria-hidden="true"
      />

      {/* Thumbnail — with checkbox overlay in selection mode */}
      <div className="flex items-center px-3 py-3 shrink-0 relative">
        {thumbnail ? (
          <Image
            src={toHttps(thumbnail)!}
            alt=""
            width={48}
            height={48}
            placeholder="blur"
            blurDataURL={BLUR_DATA_URL}
            className={cn(
              'h-12 w-12 rounded-md object-cover transition-opacity',
              isSelectionMode && isSelected && 'opacity-60'
            )}
          />
        ) : (
          <div className={cn('h-12 w-12 rounded-md bg-primary-soft transition-opacity', isSelectionMode && isSelected && 'opacity-60')} />
        )}
        {/* Checkbox overlaid on thumbnail in selection mode */}
        {isSelectionMode && (
          <div
            className={cn(
              'absolute top-2 left-2 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors shadow-sm',
              isSelected
                ? 'bg-primary border-primary'
                : 'border-white/80 bg-black/20'
            )}
            aria-hidden="true"
          >
            {isSelected && <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex items-center gap-3 pr-3 py-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-snug truncate">{title}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{subtitle}</p>
          )}
          {annotation && (
            <p className={cn(
              'text-xs italic truncate mt-1 px-2 py-1 rounded-r-md',
              'bg-[oklch(0.97_0.04_85)] border-l-2 border-accent',
              'dark:bg-[oklch(0.22_0.04_85)]'
            )}>
              {annotation}
            </p>
          )}
          {networkCount > 0 && (
            <SocialProofBadge count={networkCount} className="mt-1" />
          )}
          {lists && lists.length > 0 && <ListPills lists={lists} />}
          {footer && <div className="mt-1">{footer}</div>}
        </div>

        {/* Status pills — hidden in selection mode to reduce visual noise */}
        {showStatus && !isSelectionMode && (
          <div className="shrink-0">
            <InlineStatusPills itemId={itemId} />
          </div>
        )}
      </div>
    </>
  );

  const sharedClassName = cn(
    'flex items-stretch rounded-lg border-2 bg-card overflow-hidden transition-all',
    isSelectionMode
      ? cn(
          'cursor-pointer select-none',
          isSelected
            ? 'border-primary bg-primary/5'
            : 'border-border hover:bg-muted/40'
        )
      : 'border-border hover:shadow-sm hover:border-border/80',
    className
  );

  // In selection mode render a plain div (no navigation); in normal mode wrap
  // with a Link and attach long-press handlers.
  if (isSelectionMode) {
    return (
      <div
        className={sharedClassName}
        onClick={() => onToggleSelect?.(itemId)}
        role="checkbox"
        aria-checked={isSelected}
        tabIndex={0}
        onKeyDown={(e) => e.key === ' ' && onToggleSelect?.(itemId)}
      >
        {cardInner}
      </div>
    );
  }

  return (
    <Link
      href={link}
      className={sharedClassName}
      onPointerDown={handlePointerDown}
      onPointerUp={clearLongPress}
      onPointerLeave={clearLongPress}
      onPointerCancel={clearLongPress}
    >
      {cardInner}
    </Link>
  );
}

/** Skeleton placeholder while items load. */
export function ItemCardSkeleton() {
  return (
    <div className="flex items-stretch rounded-lg border-2 border-border bg-card overflow-hidden">
      <div className="w-1 shrink-0 bg-muted" />
      <div className="flex items-center px-3 py-3 shrink-0">
        <Skeleton className="h-12 w-12 rounded-md" />
      </div>
      <div className="flex-1 flex items-center gap-3 pr-3 py-3">
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-28" />
        </div>
      </div>
    </div>
  );
}
